import { Spinner } from "@fluentui/react-components";
import { Fragment } from "react";
import { useApiCall } from "../../../hooks/useApiCall";
import { CommonHelper } from "../../../utils/common/CommonHelper";
import {
  STATUS_COLOR_MAP,
  WEATHER_KPI_CARD_KEYS,
} from "../../../utils/weather/WeatherConstants";
import { WeatherHelper } from "../../../utils/weather/WeatherHelper";
import Card from "../../common/card/card";
import { NoDataCard } from "../../common/no-data-card/NoDataCard";
import { WeatherKPICard } from "./WeatherKPICard";

export interface IWeatherKPIProps {
  airport: string;
}

const WeatherKPI: React.FunctionComponent<IWeatherKPIProps> = ({ airport }) => {
  const getCurrentWeatherSummaryUrl = `${process.env.REACT_APP_API_WEATHER_SUMMARY?.replace(
    "{airportCode}",
    airport
  )}`;

  const {
    data: currentWeatherSummaryData,
    error,
    isLoading,
  } = useApiCall<any>({
    url: getCurrentWeatherSummaryUrl,
    dependencies: [airport],
  });

  const weatherCardArray = currentWeatherSummaryData
    ? WeatherHelper.getWeatherCardArray(
        currentWeatherSummaryData,
        WEATHER_KPI_CARD_KEYS
      )
    : [];

  const weatherCardContent = weatherCardArray?.map((array) => (
    <Fragment key={`section-key-${CommonHelper.getGuid()}`}>
      {array.map((item, i) => (
        <Card
          key={`card-${item.displayName}`}
          dataTestId={`card-${item.displayName}`}
          width={"auto"}
          height={144}
          className={`mb-2 ${(i + 1) % 4 === 0 ? "" : "mr-2"}`}
          header={<span>{item.displayName}</span>}
          headerType="default_header"
          headerAlignment="center"
          bodyType="default_body"
          footerType="default_footer"
          footer={(() => {
            switch (item.displayName) {
              case "Visibility Range":
              case "Cloud Ceiling":
                return (
                  <div
                    className="w-100"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {item.leftFooterSubBody && (
                      <div style={{ textAlign: "center" }}>
                        <div>{item.leftFooterBody}</div>
                        <div
                          style={{
                            fontSize:
                              item.displayName === "Visibility Range"
                                ? "18px"
                                : undefined,
                            fontWeight:
                              item.displayName === "Visibility Range"
                                ? 600
                                : undefined,
                            color:
                              item.displayName === "Visibility Range"
                                ? STATUS_COLOR_MAP[
                                    item.leftFooterSubBody as keyof typeof STATUS_COLOR_MAP
                                  ]
                                : undefined,
                          }}
                        >
                          {item.leftFooterSubBody}
                        </div>
                      </div>
                    )}
                  </div>
                );

              case "Precipitation":
                return (
                  <div className="w-100 amd-container-hspacebtwn">
                    <div style={{ minWidth: 120 }}>
                      <div style={{display: "flex", justifyContent: "space-between", width: "100%", marginBottom: 4,  }}>
                        <span>{item.leftFooterBody ? item.leftFooterBody.split(" ")[0] : "Rain"}</span>
                        <span>{item.leftFooterBody ? `${item.leftFooterBody.split(" ")[1]} ${item.leftFooterBody.split(" ")[2]}` : "N/A"}</span>
                      </div>

                      <div style={{display: "flex", justifyContent: "space-between", width: "100%", }}>
                        <span>{item.leftFooterSubBody ? item.leftFooterSubBody.split(" ")[0] : "Snow"}</span>
                        <span>{item.leftFooterSubBody ? `${item.leftFooterSubBody.split(" ")[1]} ${item.leftFooterSubBody.split(" ")[2]}` : "N/A"}</span>
                      </div>
                    </div>

                    <div style={{ minWidth: 120 }}>
                      <div style={{display: "flex", justifyContent: "space-between", width: "100%", marginBottom: 4, }}>
                        <span>{item.rightFooterBody ? item.rightFooterBody.split(" ")[0] : "Sleet"}</span>
                        <span>{item.rightFooterBody ? `${item.rightFooterBody.split(" ")[1]} ${item.rightFooterBody.split(" ")[2]}` : "N/A"}</span>
                      </div>

                      <div style={{display: "flex", justifyContent: "space-between", width: "100%", }}>
                        <span>{item.rightFooterSubBody ? item.rightFooterSubBody.split(" ")[0] : "Fr.Rain"}</span>
                        <span>{item.rightFooterSubBody ? `${item.rightFooterSubBody.split(" ")[1]} ${item.rightFooterSubBody.split(" ")[2]}` : "N/A"}</span>
                      </div>
                    </div>
                  </div>
                  
                );
              default:
                return (
                  <div className="w-100 amd-container-hspacebtwn">
                    <div>
                      <div>{item.leftFooterBody}</div>
                      <div>{item.leftFooterSubBody}</div>
                    </div>

                    <div>
                      <div>{item.rightFooterBody}</div>
                      <div>{item.rightFooterSubBody}</div>
                    </div>
                  </div>
                );
            }
          })()}
        >
          <WeatherKPICard
            key={`kpicard-${item.displayName}`}
            displayName={item.displayName}
            body={item.body ? item.body : "N/A"}
            unit={item.unit}
            subBody={item.subBody}
          />
        </Card>
      ))}
    </Fragment>
  ));

  return (
    <>
      {isLoading && <Spinner labelPosition="below" label="Loading..." />}
      {error && <NoDataCard message="No Weather data found" />}
      <div data-testid="weather-kpi-container" className="amd-grid-col4">
        {!error && !isLoading && weatherCardContent}
      </div>
    </>
  );
};

export default WeatherKPI;
